import { dateStringFromISO8601, propExists } from './utils';

/**
 * The display type of a report column.
 *
 * object-id / currency / date / text are the "standard" types handled by
 * formatCell and app-generic-table-cell. `custom` marks a column the report
 * renders with its own template (links, badges, ...) instead of the standard
 * cell — formatCell is not called for those (the component renders them on
 * screen; the emailed-reports renderer will use a per-column text function).
 */
export enum ReportCellType {
  objectId = 'object-id',
  currency = 'currency',
  date = 'date',
  text = 'text',
  custom = 'custom',
}

export interface CellFormatConfig {
  /** Currency symbol to prefix currency cells — see getCurrencySymbolForCurrency. */
  currencySymbol: string;
  /**
   * Timezone for date formatting. null = the runtime's local zone, which is what
   * the Staff Site uses (the staff user's browser). The Node emailed-reports
   * service has no browser, so it must pass the organization's timezone.
   */
  timezone?: string | null;
}

/**
 * Format a standard report cell's RAW value (the string returned by the export
 * `getContentItem`) into its display value — the single source of truth shared
 * by the Staff Site's app-generic-table-cell and the (Angular-free) emailed-
 * reports renderer.
 *
 * Returns `null` when the cell has no value to display, so the caller supplies
 * its own placeholder: the Staff Site renders a muted "None"; the email can
 * render "None" or leave it blank. The 'text' type never represents a missing
 * value as "None" — it simply has no value to show (null), matching the
 * existing on-screen behavior of interpolating the raw content as-is.
 *
 * The absence rules intentionally mirror app-generic-table-cell exactly:
 *   - object-id / currency: absent when the value is null/undefined (propExists)
 *   - date: absent when the value is falsy (covers empty string too)
 */
export function formatCell(rawValue: string | null | undefined,
                           columnType: ReportCellType,
                           config: CellFormatConfig): string | null {
  switch (columnType) {
    case ReportCellType.objectId:
      return propExists(rawValue) ? '#' + rawValue : null;
    case ReportCellType.currency:
      return propExists(rawValue) ? config.currencySymbol + rawValue : null;
    case ReportCellType.date:
      return rawValue ? dateStringFromISO8601(rawValue, config.timezone ?? null) : null;
    case ReportCellType.text:
    default:
      return propExists(rawValue) ? rawValue : null;
  }
}
